import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getAxiosApiV2WithAuthorization, useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../config';
import { CircularLoader } from '../../../../shared';
import { addLongPullUIItem, addNotification } from '../../../../store';

import { NgCard, NgCardContent, NgCardTitle } from '../../../UI';
import { OutputSelector } from './OutputSelector';
import { GenerateDocumentsSelector } from './GenerateDocumentsSelector';
import { DocumentsGenerateButton } from './DocumentsGenerateButton';
import { useWorkspace } from 'apps/ng/src/app/context/WorkspaceProvider';
import { useFormContext } from 'react-hook-form';

interface GenerateDocumentsSectionProps {
  projectData: any;
  entityPrimaryKey?: string;
  isQueryValid: boolean;
  isQueriesEnabled: boolean;
  isLoading: boolean;
  workspaceTemplateData?: { ID: number; DocumentTemplates: any[] };
  queriesResultsMap?: WorkspaceQueryInputResults | null;
  attributes?: { [key: string]: string }[];
}

export const GenerateDocumentsSection: FC<GenerateDocumentsSectionProps> = ({
  isLoading,
  attributes,
  projectData,
  entityPrimaryKey,
  isQueryValid,
  isQueriesEnabled,
  queriesResultsMap,
  workspaceTemplateData,
}) => {
  const dispatch = useDispatch();
  const { setLongPullItemId, isGenerating } = useWorkspace();

  const {
    setValue,
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = useFormContext() || {};

  const [totalCount, setTotalCount] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  const selectedTemplates = watch('selectedDocumentTemplates');

  const [selectedOutput, setSelectedOutput] = useState<{
    type: string;
    path: string;
    folderId?: number | null;
  } | null>({ type: 'zip', path: '/' });

  const projectTemplatesToPickList = useDeepCompareMemo(() => {
    // @todo this should be from the designed getWorkspaceTemplate endpoint
    return (workspaceTemplateData?.DocumentTemplates || [])
      .map((item) => {
        if ((item?.AllowedOutputTypes || []).includes('html')) {
          return null;
        }

        return {
          id: item?.ID,
          name: item?.DisplayName || item?.OutputType || '',
          type: item?.OutputType,
          lastUpdated: item?.UpdatedAt,
          allowedOutputTypes: item?.AllowedOutputTypes || [],
          queryID: item?.Query?.ID,
        };
      })
      .filter(Boolean);
  }, [workspaceTemplateData]);

  const generateButtonState: { disabled: boolean; message: string | null } = useDeepCompareMemo(() => {
    if (!isQueryValid) {
      return {
        disabled: true,
        message: isQueriesEnabled
          ? 'Please run a valid query before generating documents.'
          : 'Please fill in all required fields before generating documents.',
      };
    }

    if (selectedTemplates?.length <= 0) {
      return {
        disabled: true,
        message: 'Please select at least one output type to proceed.',
      };
    }

    if (totalCount > config.etl.maxDocumentBuildLimit) {
      return {
        disabled: true,
        message: `Document generation limit exceeded. Maximum allowed: ${config.etl.maxDocumentBuildLimit}`,
      };
    }

    if (isGenerating) {
      return {
        disabled: true,
        message: null,
      };
    }

    if (!isValid) {
      return {
        disabled: true,
        message: null,
      };
    }

    return {
      disabled: false,
      message: null,
    };
  }, [selectedTemplates, isGenerating, isQueryValid, totalCount, isValid]);

  const handleOutputSelectionChange = (output: { type: string; path: string }) => {
    setSelectedOutput(output);
  };

  const handleGenerateClick = async () => {
    setLongPullItemId(-1);

    try {
      setIsFetchingData(true);

      let createJobPayload: CreateJobPayload = {
        project_id: projectData.ID,
        document_templates: selectedTemplates,
        output_type: 'zip',
      };

      if (isQueriesEnabled) {
        if (!entityPrimaryKey) {
          console.error('Could not generate documents! No primary key');
          return;
        }

        const mappedAttributes = attributes?.map((item) => ({
          [entityPrimaryKey]: item[entityPrimaryKey],
          CLIENT_DISPLAY_NAME: item['CLIENT_DISPLAY_NAME'],
        }));

        createJobPayload = {
          ...createJobPayload,
          primary_key_ids: (mappedAttributes || []).map((item: any) => item[entityPrimaryKey]),
          attributes: mappedAttributes || [],
        };
      }

      if (selectedOutput?.type === 'folder') {
        createJobPayload = {
          ...createJobPayload,
          output_type: 'folder',
          output_folder_id: selectedOutput.folderId || projectData.FolderID,
        };
      }

      // generate job
      // @todo switch to rtk mutation
      const result = await getAxiosApiV2WithAuthorization({
        url: `${config.assetServiceBaseUrl}/document-build-jobs`,
        method: 'POST',
        data: createJobPayload,
      });

      // dispatch job queue - for now it will be here later it will be
      // an endpoint to dynamically get pending queries
      if (result?.data?.ID) {
        const newQueueItem = {
          id: result.data.ID,
          isUploading: true,
          isCompleted: false,
          completedFiles: 0,
          totalFiles: 0,
          message: `Starting to generate documents`,
          type: 'running',
        };

        setLongPullItemId(result.data.ID);
        dispatch(addLongPullUIItem({ item: newQueueItem }));
      } else {
        console.error('Could not start long pull: no job ID provided!');
      }

      // reset hook form to keep state for generated job
      reset(getValues());

      setIsFetchingData(false);
    } catch (error: any) {
      setIsFetchingData(false);
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error generating documents!',
        }),
      );
      console.error(error);
    }
  };


  if (projectTemplatesToPickList?.length <= 0) {
    return null;
  }

  return (
    <NgCard>
      <NgCardContent>
        {isLoading ? (
          <CircularLoader />
        ) : (
          <>
            <NgCardTitle title="Documents" />

            <GenerateDocumentsSelector
              isQueryValid={isQueryValid}
              isQueriesEnabled={isQueriesEnabled}
              options={projectTemplatesToPickList}
              queriesResultsMap={queriesResultsMap}
              onChange={({ selectedItems, totalCount }) => {
                setValue('selectedDocumentTemplates', selectedItems, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
                // handleDocumentsSelectChange({ selectedItems, totalCount });
                setTotalCount(totalCount);
              }}
            />

            <NgCardTitle title="Select output: " />

            <OutputSelector
              rootFolderId={projectData?.DocumentsFolderID}
              projectName={projectData?.DisplayName}
              onChange={handleOutputSelectionChange}
              initialOutput={selectedOutput}
            />

            <br />

            <DocumentsGenerateButton
              onClick={handleGenerateClick}
              disabled={isFetchingData || generateButtonState?.disabled}
              errorMessage={generateButtonState?.message}
              label={
                <>
                  Generate {totalCount > 0 ? totalCount : ''}
                  {totalCount === 1 ? ' document' : totalCount > 1 ? ' documents' : ''}
                </>
              }
            />
          </>
        )}
      </NgCardContent>
    </NgCard>
  );
};
